// Admin: LLM usage panel.
//
// Embedded as a tab inside AdminShell. Owns its own filters + drill-in
// drawer; the shell handles the outer chrome (tab nav, back button).
//
// Data model:
//   - Top cards: aggregate roll-up returned by GET /api/admin/usage
//     (event_count, total tokens, cache hit %, error rate, avg latency).
//   - Table: usage_events rows matching the filter, newest first.
//   - Drawer: payload row for a clicked event (request/response content).
//     30-day TTL on the server — drawer shows "expired" when 404.

import { useCallback, useEffect, useState } from 'react'
import {
  api,
  type UsageAggregate,
  type UsageEventRow,
  type UsagePayloadRow,
} from '../api'

type TaskFilter = 'all' | 'chat' | 'distill' | 'frame' | 'document'
type TierFilter = 'all' | 'flash-1m' | 'pro-200k' | 'pro-1m'
type StatusFilter = 'all' | 'ok' | 'error'

// Default window: last 7 days. Matches the server's default but we
// echo it here so the controls reflect the actual window without an
// extra round-trip on first load.
const DEFAULT_WINDOW_DAYS = 7

export function AdminUsage() {
  const [rows, setRows] = useState<UsageEventRow[] | null>(null)
  const [aggregate, setAggregate] = useState<UsageAggregate | null>(null)
  const [windowDays, setWindowDays] = useState<number>(DEFAULT_WINDOW_DAYS)
  const [task, setTask] = useState<TaskFilter>('all')
  const [tier, setTier] = useState<TierFilter>('all')
  const [status, setStatus] = useState<StatusFilter>('all')
  const [error, setError] = useState<string | null>(null)
  const [openEventId, setOpenEventId] = useState<string | null>(null)

  const reload = useCallback(async () => {
    setError(null)
    try {
      const sinceDate = new Date(Date.now() - windowDays * 24 * 60 * 60 * 1000)
      const res = await api.listAdminUsage({
        since: sinceDate.toISOString(),
        task: task === 'all' ? undefined : task,
        tierId: tier === 'all' ? undefined : tier,
        status: status === 'all' ? undefined : status,
        limit: 200,
      })
      setRows(res.events)
      setAggregate(res.aggregate)
    } catch (e) {
      setError((e as Error).message)
    }
  }, [windowDays, task, tier, status])

  useEffect(() => {
    void reload()
  }, [reload])

  return (
    <div style={{ fontSize: 13, color: 'var(--ink-1)' }}>
      <div
        style={{
          fontSize: 12,
          color: 'var(--ink-3)',
          marginBottom: 12,
        }}
      >
        last {windowDays}d
      </div>

      {/* Filters */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          marginBottom: 14,
          flexWrap: 'wrap',
        }}
      >
        <SegmentedControl
          label="window"
          options={[
            ['1d', 1],
            ['7d', 7],
            ['30d', 30],
          ]}
          value={windowDays}
          onChange={setWindowDays}
        />
        <SegmentedControl
          label="task"
          options={[
            ['all', 'all'],
            ['chat', 'chat'],
            ['distill', 'distill'],
            ['frame', 'frame'],
            ['doc', 'document'],
          ]}
          value={task}
          onChange={(v) => setTask(v as TaskFilter)}
        />
        <SegmentedControl
          label="tier"
          options={[
            ['all', 'all'],
            ['flash-1m', 'flash-1m'],
            ['pro-200k', 'pro-200k'],
            ['pro-1m', 'pro-1m'],
          ]}
          value={tier}
          onChange={(v) => setTier(v as TierFilter)}
        />
        <SegmentedControl
          label="status"
          options={[
            ['all', 'all'],
            ['ok', 'ok'],
            ['error', 'error'],
          ]}
          value={status}
          onChange={(v) => setStatus(v as StatusFilter)}
        />
        <button
          type="button"
          onClick={() => void reload()}
          style={{
            background: 'transparent',
            border: '1px solid var(--rule-s)',
            color: 'var(--ink-2)',
            padding: '3px 10px',
            borderRadius: 4,
            cursor: 'pointer',
            fontSize: 12,
            marginLeft: 'auto',
          }}
        >
          refresh
        </button>
      </div>

      {/* Aggregate cards */}
      {aggregate && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: 10,
            marginBottom: 18,
          }}
        >
          <Card label="calls" value={fmtNum(aggregate.event_count)} />
          <Card
            label="errors"
            value={fmtNum(aggregate.error_count)}
            sub={
              aggregate.event_count > 0
                ? `${pct(
                    aggregate.error_count / Math.max(1, aggregate.event_count),
                  )}%`
                : '0%'
            }
            tone={aggregate.error_count > 0 ? 'warn' : undefined}
          />
          <Card
            label="input"
            value={fmtTokens(aggregate.input_tokens)}
            sub={`+${fmtTokens(aggregate.cache_creation_tokens)} cache·write`}
          />
          <Card
            label="cache hit"
            value={`${pct(cacheHitRate(aggregate))}%`}
            sub={`${fmtTokens(aggregate.cache_read_tokens)} read`}
            tone={cacheHitRate(aggregate) >= 0.3 ? 'good' : 'warn'}
          />
          <Card
            label="output"
            value={fmtTokens(aggregate.output_tokens)}
            sub={
              aggregate.reasoning_tokens > 0
                ? `${fmtTokens(aggregate.reasoning_tokens)} reasoning (~est)`
                : 'no reasoning'
            }
          />
          <Card
            label="avg latency"
            value={
              aggregate.avg_latency_ms != null
                ? `${Math.round(aggregate.avg_latency_ms)}ms`
                : '—'
            }
          />
        </div>
      )}

      {error && (
        <div
          style={{
            background: 'var(--red-soft)',
            border: '1px solid var(--red)',
            padding: '6px 10px',
            borderRadius: 4,
            color: 'var(--red)',
            fontSize: 12,
            marginBottom: 12,
          }}
        >
          {error}
        </div>
      )}

      {rows === null ? (
        <div style={{ color: 'var(--ink-3)', fontSize: 12 }}>loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ color: 'var(--ink-3)', fontSize: 12 }}>
          no calls in the selected window
        </div>
      ) : (
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: 12,
          }}
        >
          <thead>
            <tr
              style={{
                borderBottom: '1px solid var(--rule-s)',
                textAlign: 'left',
              }}
            >
              <th style={th}>when</th>
              <th style={th}>task</th>
              <th style={th}>tier</th>
              <th style={th}>model</th>
              <th style={thNum}>in</th>
              <th style={thNum}>out</th>
              <th style={thNum}>cache r/w</th>
              <th style={thNum}>latency</th>
              <th style={th}>status</th>
              <th style={th}>user</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <UsageRowView
                key={r.id}
                row={r}
                onOpen={() => setOpenEventId(r.id)}
              />
            ))}
          </tbody>
        </table>
      )}

      {openEventId && (
        <PayloadDrawer
          eventId={openEventId}
          onClose={() => setOpenEventId(null)}
        />
      )}
    </div>
  )
}

// ── Sub-components ─────────────────────────────────────────────────

function SegmentedControl<V extends string | number>({
  label,
  options,
  value,
  onChange,
}: {
  label: string
  options: ReadonlyArray<readonly [string, V]>
  value: V
  onChange: (v: V) => void
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span
        style={{
          color: 'var(--ink-3)',
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: '.04em',
        }}
      >
        {label}
      </span>
      <div style={{ display: 'flex', gap: 4 }}>
        {options.map(([labelStr, optValue]) => (
          <button
            key={String(optValue)}
            type="button"
            onClick={() => onChange(optValue)}
            style={{
              background:
                value === optValue ? 'var(--paper-2)' : 'transparent',
              border: '1px solid var(--rule-s)',
              color: value === optValue ? 'var(--ink-1)' : 'var(--ink-3)',
              padding: '3px 10px',
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: 12,
            }}
          >
            {labelStr}
          </button>
        ))}
      </div>
    </div>
  )
}

function Card({
  label,
  value,
  sub,
  tone,
}: {
  label: string
  value: string
  sub?: string
  tone?: 'good' | 'warn'
}) {
  const valueColor =
    tone === 'good'
      ? 'var(--green, #2a8a45)'
      : tone === 'warn'
        ? 'var(--red, #c54)'
        : 'var(--ink-1)'
  return (
    <div
      style={{
        border: '1px solid var(--rule-s)',
        borderRadius: 6,
        padding: '10px 14px',
        background: 'var(--paper-1, #fafafa)',
      }}
    >
      <div
        style={{
          color: 'var(--ink-3)',
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: '.04em',
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 18, fontWeight: 600, color: valueColor }}>
        {value}
      </div>
      {sub && (
        <div style={{ color: 'var(--ink-3)', fontSize: 11, marginTop: 2 }}>
          {sub}
        </div>
      )}
    </div>
  )
}

function UsageRowView({
  row,
  onOpen,
}: {
  row: UsageEventRow
  onOpen: () => void
}) {
  const ts = new Date(row.created_at)
  const tsStr = `${ts.toLocaleDateString()} ${ts.toLocaleTimeString()}`
  return (
    <tr style={{ cursor: 'pointer' }} onClick={onOpen}>
      <td style={td}>{tsStr}</td>
      <td style={td}>{row.task}</td>
      <td style={td}>{row.tier_id ?? '—'}</td>
      <td
        style={{
          ...td,
          maxWidth: 200,
          wordBreak: 'break-all',
          color: 'var(--ink-3)',
        }}
      >
        {row.model}
      </td>
      <td style={tdNum}>{fmtTokens(row.input_tokens)}</td>
      <td style={tdNum}>{fmtTokens(row.output_tokens)}</td>
      <td style={tdNum}>
        {fmtTokens(row.cache_read_tokens)}/
        {fmtTokens(row.cache_creation_tokens)}
      </td>
      <td style={tdNum}>
        {row.latency_ms != null ? `${row.latency_ms}ms` : '—'}
      </td>
      <td style={td}>
        <span
          style={{
            background: row.status === 'ok' ? '#efe' : '#fee',
            padding: '1px 6px',
            borderRadius: 3,
            fontSize: 11,
          }}
        >
          {row.status}
        </span>
      </td>
      <td
        style={{
          ...td,
          color: 'var(--ink-3)',
          fontFamily: 'monospace',
          fontSize: 11,
        }}
      >
        {row.user_id.slice(0, 8)}
      </td>
      <td style={td}>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onOpen()
          }}
          style={{
            background: 'transparent',
            border: '1px solid var(--rule-s)',
            color: 'var(--ink-2)',
            padding: '2px 8px',
            borderRadius: 3,
            cursor: 'pointer',
            fontSize: 11,
          }}
        >
          payload
        </button>
      </td>
    </tr>
  )
}

function PayloadDrawer({
  eventId,
  onClose,
}: {
  eventId: string
  onClose: () => void
}) {
  const [payload, setPayload] = useState<UsagePayloadRow | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [expired, setExpired] = useState(false)

  useEffect(() => {
    setPayload(null)
    setError(null)
    setExpired(false)
    void (async () => {
      try {
        const p = await api.getAdminUsagePayload(eventId)
        setPayload(p)
      } catch (e) {
        const msg = (e as Error).message
        if (msg.includes('404') || msg.toLowerCase().includes('not found')) {
          setExpired(true)
        } else {
          setError(msg)
        }
      }
    })()
  }, [eventId])

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        right: 0,
        width: 'min(720px, 60vw)',
        height: '100vh',
        background: 'var(--paper-0)',
        borderLeft: '1px solid var(--rule-s)',
        boxShadow: '-4px 0 16px rgba(0,0,0,0.06)',
        overflow: 'auto',
        padding: '20px 24px',
        zIndex: 50,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: 14,
        }}
      >
        <h2 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>
          payload {eventId.slice(0, 8)}
        </h2>
        <button
          type="button"
          onClick={onClose}
          style={{
            marginLeft: 'auto',
            background: 'transparent',
            border: '1px solid var(--rule-s)',
            color: 'var(--ink-2)',
            padding: '3px 10px',
            borderRadius: 4,
            cursor: 'pointer',
            fontSize: 12,
          }}
        >
          close
        </button>
      </div>

      {expired && (
        <div style={{ color: 'var(--ink-3)', fontSize: 12 }}>
          payload expired (30-day TTL — counts row remains in the table)
        </div>
      )}
      {error && (
        <div
          style={{
            background: 'var(--red-soft)',
            border: '1px solid var(--red)',
            padding: '6px 10px',
            borderRadius: 4,
            color: 'var(--red)',
            fontSize: 12,
          }}
        >
          {error}
        </div>
      )}
      {!expired && !error && !payload && (
        <div style={{ color: 'var(--ink-3)', fontSize: 12 }}>loading…</div>
      )}
      {payload && (
        <>
          <Section title="response_text">
            <pre style={preStyle}>{payload.response_text || '(empty)'}</pre>
          </Section>
          {payload.reasoning_text && (
            <Section title="reasoning_text" defaultOpen={false}>
              <pre style={preStyle}>{payload.reasoning_text}</pre>
            </Section>
          )}
          {payload.tool_calls != null && (
            <Section title="tool_calls">
              <pre style={preStyle}>
                {JSON.stringify(payload.tool_calls, null, 2)}
              </pre>
            </Section>
          )}
          <Section title="request_messages" defaultOpen={false}>
            <pre style={preStyle}>
              {JSON.stringify(payload.request_messages, null, 2)}
            </pre>
          </Section>
          {payload.raw_usage != null && (
            <Section title="raw_usage" defaultOpen={false}>
              <pre style={preStyle}>
                {JSON.stringify(payload.raw_usage, null, 2)}
              </pre>
            </Section>
          )}
        </>
      )}
    </div>
  )
}

function Section({
  title,
  defaultOpen = true,
  children,
}: {
  title: string
  defaultOpen?: boolean
  children: React.ReactNode
}) {
  return (
    <details open={defaultOpen} style={{ marginBottom: 12 }}>
      <summary
        style={{
          cursor: 'pointer',
          fontSize: 11,
          color: 'var(--ink-3)',
          textTransform: 'uppercase',
          letterSpacing: '.04em',
          marginBottom: 4,
        }}
      >
        {title}
      </summary>
      {children}
    </details>
  )
}

// ── Helpers ────────────────────────────────────────────────────────

function fmtNum(n: number): string {
  return n.toLocaleString()
}

function fmtTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return n.toLocaleString()
}

function pct(ratio: number): string {
  return (ratio * 100).toFixed(1)
}

/** Anthropic-style cache hit rate: cache_read / (cache_read + cache_creation
 *  + uncached_input). Uncached_input is the `input_tokens` column on the
 *  Anthropic provider; for OpenAI-compat we recorded prompt-cache hits in
 *  cache_read_tokens and the rest in input_tokens, so the formula is the
 *  same. Returns 0 when there's no input at all. */
function cacheHitRate(a: UsageAggregate): number {
  const total = a.input_tokens + a.cache_creation_tokens + a.cache_read_tokens
  if (total <= 0) return 0
  return a.cache_read_tokens / total
}

const th: React.CSSProperties = {
  padding: '6px 8px',
  fontWeight: 500,
  color: 'var(--ink-3)',
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: '.04em',
}

const thNum: React.CSSProperties = { ...th, textAlign: 'right' }

const td: React.CSSProperties = {
  padding: '6px 8px',
  verticalAlign: 'top',
  borderBottom: '1px solid var(--rule-xs, #eee)',
}

const tdNum: React.CSSProperties = {
  ...td,
  textAlign: 'right',
  fontFeatureSettings: '"tnum"',
}

const preStyle: React.CSSProperties = {
  margin: '4px 0 0',
  padding: 8,
  background: 'var(--paper-1, #fafafa)',
  border: '1px solid var(--rule-s)',
  borderRadius: 4,
  fontSize: 11,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  maxHeight: 360,
  overflow: 'auto',
}
